import { henSiteBrowserUrl } from '@highereducation/request-context';
import { getApiConfig } from '@de/services';

type Location = {
  city?: string;
  countryCode?: string;
  country?: string;
  dmaCode?: number;
  ip?: string;
  latitude?: number;
  longitude?: number;
  regionCode?: string;
  state?: string;
  timeZone?: string;
  zipCode?: string;
  ident?: string;
};

// Trailing slash is required: hen-site runs with `trailingSlash: true`, so a
// request to the slash-less path is 308-redirected to the canonical
// trailing-slash form. Calling the canonical path directly avoids that hop.
const LOCATE_PATH = '/api/v1/locate/';

let inFlightLocationInference: Promise<Location | null> | null = null;

type StoredLocationInference = {
  pathname: string;
  location: Location;
};

// Kept for the current document only. This module can also load in the
// server runtime, where a retained result would leak one visitor's location
// into a later request.
let storedLocationInference: StoredLocationInference | null = null;

const isBrowser = (): boolean => typeof window !== 'undefined';

export const clearStoredLocationInference = (): void => {
  storedLocationInference = null;
};

const requestLocationInference = async (): Promise<Location | null> => {
  const baseUrl = getApiConfig().locationInferenceApiUrl ?? '';

  try {
    const response = await fetch(henSiteBrowserUrl(baseUrl, LOCATE_PATH), {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      return null;
    }

    return (await response.json()) as Location;
  } catch {
    return null;
  }
};

/**
 * Infers the visitor's location from their IP via the hen-site BFF
 * (`GET /api/v1/locate`), which queries TIP server-side. This replaces the
 * deprecated Mobius `/v1/locate` call — there is intentionally no Mobius
 * fallback and no `idToken` (the endpoint is a tenant-agnostic IP lookup).
 *
 * `locationInferenceApiUrl` is `''` (same-origin) for O&O tenants and the
 * absolute hen-site origin for affiliate embeds, which makes this request
 * cross-origin. It must therefore stay a **CORS simple request**: only the
 * safelisted `Accept` header is sent. Any non-safelisted header (notably the
 * `X-RVEDU-*` / `X-Powered-By` service-tracking set) would force a preflight,
 * and the hen-site edge answers an `OPTIONS` carrying those headers without
 * `Access-Control-Allow-*` — the browser would then block the call before the
 * `GET` is ever issued. The endpoint reads none of those headers; it keys
 * solely off the server-resolved client IP.
 *
 * Returns `null` on any non-OK response or error so callers degrade gracefully.
 */
export const getLocationInference = (): Promise<Location | null> => {
  if (isBrowser()) {
    const pathname = window.location.pathname;
    if (storedLocationInference?.pathname === pathname) {
      return Promise.resolve(storedLocationInference.location);
    }
    storedLocationInference = null;
  }

  if (inFlightLocationInference) {
    return inFlightLocationInference;
  }

  const pathnameAtStart = isBrowser() ? window.location.pathname : null;
  const request = requestLocationInference().then((location) => {
    if (location && pathnameAtStart !== null && isBrowser()) {
      storedLocationInference = { pathname: pathnameAtStart, location };
    }
    return location;
  });
  inFlightLocationInference = request;
  void request.finally(() => {
    if (inFlightLocationInference === request) {
      inFlightLocationInference = null;
    }
  });
  return request;
};
